"use client";

import React from "react";
import type { CompanyEntity } from "@/lib/types/company";
import { AISummary } from "@/components/ai/ai-summary";
import { COMPANY_TIMELINE_SUMMARY_OPTIONS } from "@/lib/domain/crm-entity-relationships";
import { CompanyExecutiveSummary } from "./CompanyExecutiveSummary";
import { CompanyKeyFacts } from "./CompanyKeyFacts";
import { CompanyContactInfo } from "./CompanyContactInfo";
import { CompanyBusinessDetails } from "./CompanyBusinessDetails";
import { CompanyProjectHealth } from "./CompanyProjectHealth";
import { CompanyTechnologies } from "./CompanyTechnologies";
import { CompanySocialLinks } from "./CompanySocialLinks";
import { CompanyNotes } from "./CompanyNotes";
import { CompanyContactsQuickView } from "./CompanyContactsQuickView";

interface CompanyOverviewProps {
  company: CompanyEntity;
  onRefresh?: () => void;
}

/**
 * CompanyOverview - Executive-friendly company information display
 * 
 * Presents comprehensive company information in a scannable, digestible format
 * for salespeople and executives who need to quickly understand:
 * - What the company does (executive summary)
 * - Key business metrics (revenue, size, etc.)
 * - How to contact them
 * - Business relationship details (rates, contracts)
 * - Project health status
 * - Technologies they use
 * - Social media presence
 * - Key contacts with quick email access
 * - Additional notes
 * - CRM-wide AI briefing (roster + communications + pipeline)
 *
 * This component replaces the basic contact-focused display with a full
 * company intelligence view that helps sales and leadership make informed decisions.
 */
export function CompanyOverview({ company, onRefresh }: CompanyOverviewProps) {
  return (
    <div className="space-y-6">
      <AISummary
        entityType="company"
        entityId={company.id}
        summaryType="timeline"
        options={{ ...COMPANY_TIMELINE_SUMMARY_OPTIONS }}
        showMetadata={true}
        showRefreshButton={true}
        className="border-0 shadow-none px-0"
      />

      {/* Hero Section - Executive Summary takes full width */}
      <CompanyExecutiveSummary company={company} onRefresh={onRefresh} />

      {/* Key Information Grid - 2 columns on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CompanyKeyFacts company={company} />
        <CompanyContactInfo company={company} />
      </div>

      {/* Business & Health Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CompanyBusinessDetails company={company} />
        <CompanyProjectHealth company={company} onRefresh={onRefresh} />
      </div>

      {/* Key Contacts Preview - Full width for list display */}
      <CompanyContactsQuickView company={company} onRefresh={onRefresh} />

      {/* Technical & Social */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CompanyTechnologies company={company} />
        <CompanySocialLinks company={company} />
      </div>

      {/* Notes - Full width for extended content */}
      <CompanyNotes company={company} />
    </div>
  );
}
